// hooks
import { useCallback, useEffect, useState, Fragment } from "react";
import useHttp from "hooks/use-http";
import { useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";

// styled component
import {
  StyledStack,
  StyledMenuItem,
  StyledGridIcons,
} from "./displayArticles.style";

// material styles
import {
  Grid,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";

// material icons
import { Delete, Edit, FilterAlt } from "@mui/icons-material";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";

// helper
import GenerateHeadCell from "helper/generate-head-cell";

// constants
import { PAGINATION_LIMIT } from "constants";

// request methods
import {
  getArticleCategoryRequest,
  getArticlesDataRequest,
} from "api/requests/articles";

// products head cells
const headCells = GenerateHeadCell("articles_list");

function DisplayArticles() {
  const { request, loading, setLoading } = useHttp(true);

  const navigate = useNavigate();

  const {
    register,
    control,
    formState: { errors },
  } = useForm();

  // states for filtered
  const [searchValue, setSearchValue] = useState("");

  // articles data state
  const [articlesData, setArticlesData] = useState({
    data: [],
    count: 0,
  });

  // page state for pagination
  const [page, setPage] = useState(1);

  // state for filter
  const [categoryList, setCategoryList] = useState({
    data: [],
    count: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState();

  // handle date
  const dateHandler = (value) =>
    value
      .split(" ")[0]
      .split("-")
      .map((item) => item)
      .join("/");

  // get all articles data
  const getArticlesData = async () => {
    const categoryRes = await request({
      url: getArticleCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });

    if (categoryRes) {
      let transformedData = categoryRes.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setCategoryList({
        data: transformedData,
        count: categoryRes.data.length,
      });
    }
    const values = {
      skip: searchValue ? 0 : (page - 1) * PAGINATION_LIMIT,
      limit: searchValue ? 100 : PAGINATION_LIMIT,
      search: searchValue ?? null,
      category: selectedCategory ?? null,
    };
    !searchValue.length && delete values.search;
    !selectedCategory && delete values.category;

    const res = await request({
      url: getArticlesDataRequest,
      values,
      // isMulty: true,
    });

    setLoading(false);

    if (res) {
      if (res.data.count < 1) {
        return setArticlesData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        title: items.title,
        category: items.category?.title ? items.category?.title : "ثبت نشده",
        date: dateHandler(items.created_at.jdate),
        edit: (
          <IconButton
            sx={{ color: "#1B569A" }}
            onClick={() => {
              navigate("/articles/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setArticlesData({
        data: transformedData,
        count: res.data.count,
      });
    }
    setLoading(false);
  };

  const getArticlesDataWithoutFilter = async () => {
    setSelectedCategory("");
    setSearchValue("");

    const res = await request({
      url: getArticlesDataRequest,
      values: {
        skip: (page - 1) * PAGINATION_LIMIT,
        limit: PAGINATION_LIMIT,
      },
      isMulty: true,
    });

    setLoading(false);

    if (res) {
      if (res.data.count < 1) {
        return setArticlesData({
          count: res.data.count,
          data: [{ content: "هیچ اطلاعاتی یافت نشد" }],
        });
      }

      let transformedData = res.data.data.map((items) => ({
        id: items.id,
        title: items.title,
        category: items.category?.title ? items.category?.title : "ثبت نشده",
        date: dateHandler(items.created_at.jdate),
        edit: (
          <IconButton
            sx={{ color: "#1B569A" }}
            onClick={() => {
              navigate("/articles/edit/" + items.id);
            }}
          >
            <Tooltip title="ویرایش" placement="top">
              <Edit />
            </Tooltip>
          </IconButton>
        ),
      }));

      setArticlesData({
        data: transformedData,
        count: res.data.count,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    getArticlesData();
  }, [page]);

  return (
    <DrawerCM title="اخبار و مقالات">
      {loading ? (
        <LoadingCM />
      ) : (
        <Fragment>
          <StyledStack>
            <Grid container>
              <Grid item sm={6} xs={12} p={0.5}>
                <TextField
                  label="جست و جو"
                  fullWidth
                  id="outlined-size-small"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select_category"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select_category">سردسته</InputLabel>
                      <Select
                        id="category"
                        name="category"
                        label="سردسته"
                        labelId="select-category"
                        value={selectedCategory ?? ""}
                        {...register("category")}
                        error={Boolean(errors.category)}
                        helperText={errors.category?.message}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {categoryList?.data?.map((item, index) => (
                          <StyledMenuItem key={index} value={item.id}>
                            {item.title}
                          </StyledMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <StyledGridIcons item sm={2} xs={12} p={0.5}>
                <IconButton color="primary" onClick={getArticlesData}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton
                  color="error"
                  onClick={getArticlesDataWithoutFilter}
                >
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                title="لیست اخبار"
                rows={articlesData.data}
                headCells={headCells}
                page={page}
                addArticles
                addArticlesCategory
                colspan={articlesData.count === 0 ? "11" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
            </Grid>
            {articlesData.count < 1 ? (
              ""
            ) : (
              <PaginationCM
                handleChange={(_, val) => setPage(val)}
                page={page}
                count={articlesData.count}
              />
            )}
          </Grid>
        </Fragment>
      )}
    </DrawerCM>
  );
}

export default DisplayArticles;
